import React, { PropsWithChildren } from 'react';

import { css, cx } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles';
import { GlycemiaParametersType } from '@/models/DiabetesDataModel';

export type ThresholdStep = GlycemiaParametersType | 'normal';

export type ThresholdStepProps = PropsWithChildren<{
  id?: string;
  threshold: ThresholdStep;
  className?: string;
}>;

export const ThresholdStep: React.FC<ThresholdStepProps> = ({
  id,
  threshold,
  children,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  return (
    <div id={id} className={cx(styles.container, className)}>
      <div className={cx(styles.box, styles[threshold])}>
        <Typography variant="body">
          {t(`thresholdSettings.${threshold}`)}
        </Typography>
      </div>
      {children}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css``,
  box: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    flex: 1 1;
    height: 5rem;
    aspect-ratio: 3;
    border-radius: ${theme.spacing(4)};
    padding: ${theme.spacing(2)};
    overflow: clip;
  `,
  hypoglycemia: css`
    background-color: ${theme.palette.glycemia.hypoglycemia.contrastText};
    border: 1px solid ${theme.palette.glycemia.hypoglycemia.main};
  `,
  hyperglycemia: css`
    background-color: ${theme.palette.glycemia.hyperglycemia.contrastText};
    border: 1px solid ${theme.palette.glycemia.hyperglycemia.main};
  `,
  severeHypoglycemia: css`
    background-color: ${theme.palette.glycemia.severeHypoglycemia.contrastText};
    border: 1px solid ${theme.palette.glycemia.severeHypoglycemia.main};
  `,
  severeHyperglycemia: css`
    background-color: ${theme.palette.glycemia.severeHyperglycemia
      .contrastText};
    border: 1px solid ${theme.palette.glycemia.severeHyperglycemia.main};
  `,
  normal: css`
    background-color: ${theme.palette.glycemia.normal.contrastText};
    border: 1px solid ${theme.palette.glycemia.normal.main};
  `,
});
